import useArrayFilters from 'hooks/useArrayFilters';
import { useEffect, useRef } from 'react';

/**
 * Integration list for Pricing Calculator
 * Based on the Service Admin Secret Mapping table
 */
export interface PricingCalculatorIntegration {
  value: string;
  name: string;
  url?: string;
}

const DOCS_BASE = 'https://readme.mosaicapp.com';

export const pricingCalculatorIntegrations: PricingCalculatorIntegration[] = [
  { value: 'adp-run', name: 'ADP Run' },
  { value: 'airtable', name: 'Airtable' },
  {
    value: 'deltek-ajera-cloud',
    name: 'Ajera Cloud without timesheet',
    url: `${DOCS_BASE}/docs/deltek-ajera-cloud-integration`,
  },
  {
    value: 'deltek-ajera-cloud-odbc',
    name: 'Ajera Cloud (Requires an Ajera Administrative license $2.5k/yr)',
    url: `${DOCS_BASE}/docs/deltek-ajera-cloud-integration`,
  },
  {
    value: 'deltek-ajera-on-prem',
    name: 'Ajera On-Prem (SQL Server)',
    url: `${DOCS_BASE}/docs/deltek-ajera-on-premise-integration`,
  },
  {
    value: 'deltek-ajera-on-prem-sql',
    name: 'Ajera On-Prem (Express SQL Server)',
    url: `${DOCS_BASE}/docs/deltek-ajera-on-premise-integration`,
  },
  { value: 'asana', name: 'Asana', url: `${DOCS_BASE}/docs/asana-integration` },
  { value: 'bamboo-hr', name: 'BambooHR' },
  { value: 'bqe-core', name: 'BQE Core', url: `${DOCS_BASE}/docs/bqe-core-integration` },
  {
    value: 'bst-global-cloud',
    name: 'BST Global Cloud',
    url: `${DOCS_BASE}/docs/bst10-integration`,
  },
  {
    value: 'bst-global-sql',
    name: 'BST Global (SQL Server)',
    url: `${DOCS_BASE}/docs/bst10-integration`,
  },
  { value: 'cmix', name: 'CMIX' },
  {
    value: 'costpoint',
    name: 'Costpoint',
    url: `${DOCS_BASE}/docs/deltek-costpoint-cloud-integration`,
  },
  { value: 'deltek-pim', name: 'Deltek PIM', url: `${DOCS_BASE}/docs/deltek-pim-integration` },
  {
    value: 'deltek-vantagepoint-cloud',
    name: 'Deltek Vantagepoint Cloud',
    url: `${DOCS_BASE}/docs/deltek-vantagepoint-cloud-integration-setup-v2`,
  },
  {
    value: 'dynamic365-business-central',
    name: 'Dynamics 365 Business Central',
  },
  { value: 'dynamic365-on-prem', name: 'Dynamics 365 On-Prem' },
  {
    value: 'dynamic365-project-operations',
    name: 'Dynamics 365 Project Operations',
    url: `${DOCS_BASE}/docs/microsoft-dynamics-365-project-operations-integration`,
  },
  { value: 'financial-force', name: 'Financial Force' },
  { value: 'freshbooks', name: 'Freshbooks' },
  { value: 'github', name: 'GitHub', url: `${DOCS_BASE}/docs/github-integration` },
  { value: 'google-calendar', name: 'Google Calendar' },
  { value: 'harvest-timesheet', name: 'Harvest Timesheet' },
  { value: 'jira-cloud', name: 'Jira Cloud', url: `${DOCS_BASE}/docs/jira-cloud-integration` },
  { value: 'jira-on-prem', name: 'Jira On-Prem' },
  { value: 'jira-server', name: 'Jira Server' },
  { value: 'merge-integration', name: 'Merge Supported Integration' },
  { value: 'ms-calendar', name: 'Microsoft Calendar' },
  { value: 'monday-com', name: 'Monday.com' },
  { value: 'ms-project-online', name: 'MS Project Online' },
  { value: 'netsuite-cloud', name: 'Netsuite Cloud' },
  { value: 'new-custom-api', name: 'New Custom API' },
  { value: 'open-api', name: 'API + MCP', url: `${DOCS_BASE}/reference/authentication` },
  { value: 'oracle-cloud', name: 'Oracle Cloud' },
  { value: 'oracle-fusion', name: 'Oracle Fusion' },
  { value: 'oracle-hyperion', name: 'Oracle Hyperion' },
  { value: 'oracle-on-prem', name: 'Oracle On-Prem' },
  { value: 'paylocity', name: 'Paylocity' },
  { value: 'primavera-cloud', name: 'Primavera Cloud' },
  { value: 'primavera-on-prem', name: 'Primavera On-Prem' },
  { value: 'procore', name: 'Procore' },
  { value: 'product-board', name: 'Product Board' },
  {
    value: 'quickbooks-desktop',
    name: 'QB Desktop',
    url: `${DOCS_BASE}/docs/quickbooks-desktop-integration`,
  },
  {
    value: 'quickbooks-online',
    name: 'QB Online',
    url: `${DOCS_BASE}/docs/quickbooks-online-integration`,
  },
  { value: 'quickbase', name: 'Quickbase' },
  { value: 'replicon', name: 'Replicon' },
  { value: 'sage-intacct', name: 'Sage Intacct' },
  { value: 'salesforce', name: 'Salesforce', url: `${DOCS_BASE}/docs/salesforce-integration` },
  { value: 'sap-on-prem', name: 'SAP On-Prem' },
  { value: 'slack', name: 'Slack' },
  { value: 'ms-teams', name: 'Teams' },
  { value: 'ukg', name: 'UKG' },
  { value: 'unanet', name: 'Unanet A/E', url: `${DOCS_BASE}/docs/unanet-ae-cloud-integration` },
  { value: 'unanet-ae', name: 'Unanet Gov', url: `${DOCS_BASE}/docs/unanet-govcon-integration` },
  {
    value: 'deltek-vantagepoint-on-prem',
    name: 'Vantagepoint On-Prem (SQL Server)',
    url: `${DOCS_BASE}/docs/deltek-vantagepoint-on-premise-integration`,
  },
  {
    value: 'deltek-vision-cloud',
    name: 'Vision Cloud',
    url: `${DOCS_BASE}/docs/deltek-vision-cloud-integration`,
  },
  {
    value: 'deltek-vision-on-prem',
    name: 'Vision On-Prem (SQL Server)',
    url: `${DOCS_BASE}/docs/deltek-vision-on-premise-integration`,
  },
  { value: 'viewpoint-vista-cloud', name: 'Vista Cloud' },
  {
    value: 'viewpoint-vista-on-prem',
    name: 'Vista (SQL Server)',
    url: `${DOCS_BASE}/docs/vista-by-viewpoint-on-premise-integration`,
  },
  { value: 'workamajig', name: 'Workamajig' },
  { value: 'workday', name: 'Workday' },
  { value: 'xero', name: 'Xero' },
];

export const pricingCalculatorIntegrationsHash = pricingCalculatorIntegrations.reduce(
  (acc, integration) => {
    acc[integration.value] = integration;
    return acc;
  },
  {} as Record<string, PricingCalculatorIntegration>,
);

export const pricingCalculatorIntegrationsArray = [...pricingCalculatorIntegrations]
  .sort((a, b) => a.name.localeCompare(b.name))
  .map((integration) => integration.value);

export const usePricingCalculatorIntegrationsMultiSelector = ({
  integrationsToExclude = [],
  initialSelectedIntegrations = [],
  onChange,
}: {
  integrationsToExclude?: string[];
  initialSelectedIntegrations?: string[];
  onChange?: (selectedIntegrations: string[]) => void;
}) => {
  const { items, selectedItems, onSelect } = useArrayFilters({
    items:
      integrationsToExclude.length !== 0
        ? pricingCalculatorIntegrationsArray.filter(
            (integration) => !integrationsToExclude.includes(integration),
          )
        : pricingCalculatorIntegrationsArray,
  });

  // Track if initialization has already occurred
  const hasInitializedRef = useRef(false);

  // Initialize with provided integrations on mount
  useEffect(() => {
    if (
      !hasInitializedRef.current &&
      initialSelectedIntegrations.length > 0 &&
      selectedItems.length === 0
    ) {
      hasInitializedRef.current = true;

      // Filter out integrations that don't exist in the pricing calculator list
      const validIntegrations = initialSelectedIntegrations.filter(
        (integration) => pricingCalculatorIntegrationsHash[integration],
      );

      if (validIntegrations.length > 0) {
        onSelect(validIntegrations);
      }
    }
  }, [initialSelectedIntegrations, selectedItems, onSelect]);

  // Notify parent component when selected items change
  useEffect(() => {
    if (onChange) {
      onChange(selectedItems);
    }
  }, [selectedItems, onChange]);

  return {
    selectedIntegrations: selectedItems,
    onSelectIntegration: onSelect,
    integrationItems: items,
    labelKey: 'name' as const,
  };
};
